import React, { useState, useRef, useEffect } from "react";

import { Divider } from "Components/atoms/Divider";
import { FlightPrice } from "Components/molecules/FlightPrice";
import { FlightSector } from "Components/molecules/FlightSector/Desktop";
import { ExpandedFlightCard } from "Components/organisms/ExpandedFlightCard";
import { ExpandedFlightButton } from "../../ExpandedFlightButton";

import {
  FlightCardContainer,
  SegmentsContainer,
  FlightCardWrapper,
  PriceContainer,
} from "./styles";

import { FlightCardProps } from "./types";
import { sendGTMEvent } from "shared/services/GTM";
import { convertDateToDDMMAAAA } from "shared/utils/dates";
import { extractAirlines } from "shared/utils/extractAirlines";
import { useSearchContext } from "shared/contexts/SearchContext";
import { getDeeplink } from "shared/utils/DeepLink/getDeepLink";

/**
 * Componente FlightCard
 *
 * Novo comportamento:
 * - Clique no conteúdo do card → abre o seller mais barato
 * - Clique no preço → exibe apenas os detalhes do voo
 * - Clique em "Ver outros vendedores" → exibe apenas os sellers
 */
export const FlightCard: React.FC<FlightCardProps> = ({
  cardResult,
  resultPosition = { indexPosition: 1, currentPage: 1 },
}) => {
  const { searchState } = useSearchContext();

  const [expandedSection, setExpandedSection] = useState<
    "details" | "sellers" | null
  >(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expandedSection && cardRef.current) {
      const topOffset =
        cardRef.current.getBoundingClientRect().top + window.scrollY - 10;

      window.scrollTo({ top: topOffset, behavior: "smooth" });
    }
  }, [expandedSection]);

  /**
   * Evento GTM
   */
  const handleTriggerEvent = () => {
    sendGTMEvent("flightCard", {
      resultsPosition: resultPosition?.indexPosition + 1,
      page: resultPosition?.currentPage,
      airlines: extractAirlines(cardResult.sectors),
      minPrice: cardResult.minPrice,
      IATA_origin: cardResult.sectors[0].departureAirportIata,
      IATA_destination: cardResult.sectors[0].arrivalAirportIata,
      departure_date: convertDateToDDMMAAAA(
        cardResult.sectors[0].departureDate,
      ),
      return_date: cardResult.sectors[1]
        ? convertDateToDDMMAAAA(cardResult.sectors[1].departureDate)
        : "",
    });
  };

  /**
   * Abre o seller com o menor preço disponível no card.
   */
  const handleOpenFirstSeller = () => {
    const cheapestSeller = cardResult?.sellers?.reduce((cheapest, seller) =>
      seller.price < cheapest.price ? seller : cheapest,
    );
    if (!cheapestSeller) return;

    handleTriggerEvent();

    window.open(
      getDeeplink(cardResult, cheapestSeller),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleShowDetails = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setExpandedSection((currentSection) => (currentSection ? null : "details"));
  };

  const handleShowSellers = () => setExpandedSection("sellers");
  const handleClose = () => setExpandedSection(null);

  const handleWhereToBuy = () => {
    if (expandedSection === "sellers") {
      handleOpenFirstSeller();
      return;
    }

    setExpandedSection("sellers");
  };

  return (
    <FlightCardWrapper
      ref={cardRef}
      isOneWay={searchState.tripType === "oneWay"}
    >
      <FlightCardContainer onClick={handleOpenFirstSeller}>
        <SegmentsContainer>
          <FlightSector {...cardResult?.sectors[0]} />

          {searchState.tripType !== "oneWay" && cardResult?.sectors[1] && (
            <>
              <Divider styleType="dashed" />
              <FlightSector {...cardResult?.sectors[1]} />
            </>
          )}
        </SegmentsContainer>

        <PriceContainer onClick={handleShowDetails}>
          <FlightPrice
            price={cardResult?.minPrice}
            currency={"R$"}
            onButtonClick={handleShowDetails}
          />
        </PriceContainer>
      </FlightCardContainer>

      {expandedSection ? (
        <ExpandedFlightCard
          cardResult={cardResult}
          mode={expandedSection}
          onClose={handleClose}
          onWhereToBuy={handleWhereToBuy}
        />
      ) : (
        <ExpandedFlightButton
          sellersCount={cardResult.sellers.length}
          onClick={handleShowSellers}
        />
      )}
    </FlightCardWrapper>
  );
};
